import type { Manhole, StormSewerRun, LateralConnection, GullyConnection, FoulBuildUpRowData } from '../types/tender.types';
import { backfillLabel, backfillEvalCode } from './backfillTypes';
import { findSewerRate } from './sewerRatesData';
import { findManholeRateVolume } from './manholeRatesVolumeData';
import type { StormBuildUpRowDef } from './stormBuildUpTypes';

// ── Types ─────────────────────────────────────────────────────────────────────
export type StormSummaryRowType = 'section' | 'pipe-type' | 'item';

export interface StormFormulaInfoRow { ref: string; value: number; unit: string }

export interface StormFormulaInfo {
  label: string;
  formula: string;
  rows: StormFormulaInfoRow[];
  total: number;
}

export interface StormSummaryRow {
  key: string;
  rowType: StormSummaryRowType;
  reference?: string;
  description?: string;
  quantity?: string;
  unit?: string;
  evaluateCode?: string;
  formulaInfo?: StormFormulaInfo;
  children?: StormSummaryRow[];
  /** Bold label row within a build-up section (e.g. Weholite's "L&P for Tank
   * Installation & Turrets") — no qty/unit/rate/amount, same as `StormBuildUpRowDef.isSubHeader`. */
  isSubHeader?: boolean;
}

/**
 * Drop every leaf ('item') row whose quantity is blank or zero, then drop any
 * 'section'/'pipe-type' group whose children became empty as a result —
 * these builder functions push every row/group unconditionally regardless of
 * quantity, so a row with nothing entered (or computed to 0) otherwise shows
 * as a near-blank line in the Bill Summary and BOQ export. A row with no
 * `quantity` key at all (a group header, a `isSubHeader` label row, or an
 * informational placeholder like "No pipe runs entered yet") is never a
 * candidate for removal here — only rows the builder explicitly gave a
 * quantity string to.
 *
 * `roundToWhole` must match how the CALLING builder's own tree is displayed:
 * `buildStormSummaryData` feeds StormSewersBillSummary.tsx, whose Qty column
 * shows `Math.round(n)` (a whole number) — a raw 0.3 there reads as "0" on
 * screen despite not being `=== 0`, so pass `true` (the default). But
 * `buildStormBuildUpSummaryData` feeds StormBuildUpBillSummary.tsx (Ponds &
 * Swales / Cellular / Weholite / Conc Attenuation / Box Culverts), whose Qty
 * column shows the value to 2dp via `fmt()` — there a genuine 0.30 reads as
 * "0.30", not "0", so rounding before the zero-check would wrongly hide a row
 * the user can plainly see is non-zero; pass `false`. The BOQ export always
 * displays its own already-whole-number-rounded `quantity` regardless of this
 * flag — see `addLiveDomainSheet` in ReviewCalculate.tsx, which prunes on
 * that rounded value independently.
 */
function pruneZeroQuantityStormRows(rows: StormSummaryRow[], roundToWhole: boolean = true): StormSummaryRow[] {
  return rows.reduce<StormSummaryRow[]>((acc, row) => {
    if (row.children && row.children.length > 0) {
      const prunedChildren = pruneZeroQuantityStormRows(row.children, roundToWhole);
      if (prunedChildren.length > 0) acc.push({ ...row, children: prunedChildren });
      return acc;
    }
    if (row.rowType === 'item' && !row.isSubHeader && row.quantity !== undefined) {
      const trimmed = row.quantity.trim();
      if (trimmed === '') return acc;
      const n = parseFloat(trimmed);
      if ((roundToWhole ? Math.round(n) : n) === 0) return acc;
    }
    acc.push(row);
    return acc;
  }, []);
}

// ── Lookup helpers ────────────────────────────────────────────────────────────
// Backfill label / eval-code live in the shared source of truth so a rename
// (e.g. "Type 1" → "Mot Type 1") only ever touches one file. Re-exported here so
// existing importers of this module keep working.
export { backfillLabel, backfillEvalCode };

export const pipeTypeEvalCode = (pt: string): string => {
  const m: Record<string, string> = { clay: 'PS', ultra: 'PU', concrete: 'PM', rigi: 'PR' };
  return m[pt?.toLowerCase()] ?? '';
};

// ── Quantity helpers ──────────────────────────────────────────────────────────
export const calcStormRockerQty = (
  pipeRuns: StormSewerRun[],
  material: string,
  pipeDiameter: number,
): number =>
  pipeRuns
    .filter(
      (r) =>
        (r.pipeType ?? '').toLowerCase() === material.toLowerCase() &&
        parseInt(r.diameter ?? '', 10) === pipeDiameter,
    )
    .reduce((sum, r) => sum + (r.rockers ?? 0), 0);

export const calcStormConcreteProtection = (
  pipeRuns: StormSewerRun[],
  pipeDiameter: number,
): number =>
  pipeRuns
    .filter((r) => parseInt(r.diameter ?? '', 10) === pipeDiameter)
    .reduce((sum, r) => sum + (r.concreteProtection ?? 0), 0);

export const calcStormLadderMetres = (manholes: Manhole[]): number =>
  manholes.reduce((sum, mh) => sum + (mh.ladders ?? 0), 0);

export const calcStormChainMetres = (manholes: Manhole[]): number =>
  manholes.reduce((sum, mh) => sum + (mh.safetyChains ?? 0), 0);

// ── Static template data ──────────────────────────────────────────────────────
export const STORM_STUB_PIPE_GROUPS: Array<{
  key: string; label: string; pipeType: string;
  items: Array<{ code: string; desc: string; dia: number }>;
}> = [
  {
    key: 'sstub-supersleeve', label: 'Supersleeve', pipeType: 'clay',
    items: [
      { code: 'APS100R',  desc: 'EO for 100mmØ Stub / rocker pipe', dia: 100 },
      { code: 'APS150R',  desc: 'EO for 150mmØ Stub / rocker pipe', dia: 150 },
      { code: 'APS225R',  desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APS300R',  desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
    ],
  },
  {
    key: 'sstub-ultrarib', label: 'Ultrarib', pipeType: 'ultra',
    items: [
      { code: 'APU225SR', desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APU300SR', desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
    ],
  },
  {
    key: 'sstub-ridgisewer', label: 'Ridgisewer', pipeType: 'rigi',
    items: [
      { code: 'APRS150SR', desc: 'EO for 150mmØ Stub / rocker pipe', dia: 150 },
      { code: 'APRS225SR', desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APRS300SR', desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
      { code: 'APRS400SR', desc: 'EO for 400mmØ Stub / rocker pipe', dia: 400 },
      { code: 'APRS450SR', desc: 'EO for 450mmØ Stub / rocker pipe', dia: 450 },
      { code: 'APRS500SR', desc: 'EO for 500mmØ Stub / rocker pipe', dia: 500 },
      { code: 'APRS600SR', desc: 'EO for 600mmØ Stub / rocker pipe', dia: 600 },
      { code: 'APRS750SR', desc: 'EO for 750mmØ Stub / rocker pipe', dia: 750 },
      { code: 'APRS900SR', desc: 'EO for 900mmØ Stub / rocker pipe', dia: 900 },
    ],
  },
  {
    key: 'sstub-concrete', label: 'Concrete', pipeType: 'concrete',
    items: [
      { code: 'APM300SR',  desc: 'EO for 300mmØ Stub / rocker pipe',  dia: 300  },
      { code: 'APM450SR',  desc: 'EO for 450mmØ Stub / rocker pipe',  dia: 450  },
      { code: 'APM525SR',  desc: 'EO for 525mmØ Stub / rocker pipe',  dia: 525  },
      { code: 'APM600SR',  desc: 'EO for 600mmØ Stub / rocker pipe',  dia: 600  },
      { code: 'APM675SR',  desc: 'EO for 675mmØ Stub / rocker pipe',  dia: 675  },
      { code: 'APM750SR',  desc: 'EO for 750mmØ Stub / rocker pipe',  dia: 750  },
      { code: 'APM825SR',  desc: 'EO for 825mmØ Stub / rocker pipe',  dia: 825  },
      { code: 'APM900SR',  desc: 'EO for 900mmØ Stub / rocker pipe',  dia: 900  },
      { code: 'APM1050SR', desc: 'EO for 1050mmØ Stub / rocker pipe', dia: 1050 },
      { code: 'APM1200SR', desc: 'EO for 1200mmØ Stub / rocker pipe', dia: 1200 },
      { code: 'APM1350SR', desc: 'EO for 1350mmØ Stub / rocker pipe', dia: 1350 },
      { code: 'APM1500SR', desc: 'EO for 1500mmØ Stub / rocker pipe', dia: 1500 },
      { code: 'APM1800SR', desc: 'EO for 1800mmØ Stub / rocker pipe', dia: 1800 },
      { code: 'APM2100SR', desc: 'EO for 2100mmØ Stub / rocker pipe', dia: 2100 },
      { code: 'APM2400SR', desc: 'EO for 2400mmØ Stub / rocker pipe', dia: 2400 },
    ],
  },
];

export const STORM_JUNCTION_GROUPS: Array<{
  key: string; label: string; pipeType: string;
  items: Array<{ code: string; desc: string; mainDia: string; branchDia: string }>;
}> = [
  {
    key: 'sjunc-supersleeve', label: 'Supersleeve', pipeType: 'clay',
    items: [
      { code: 'APS100J1', desc: 'EO for 100 x 100mm junction', mainDia: '100mm', branchDia: '100mm' },
      { code: 'APS150J1', desc: 'EO for 150 x 100mm junction', mainDia: '150mm', branchDia: '100mm' },
      { code: 'APS150J2', desc: 'EO for 150 x 150mm junction', mainDia: '150mm', branchDia: '150mm' },
      { code: 'APS225J1', desc: 'EO for 225 x 100mm junction', mainDia: '225mm', branchDia: '100mm' },
      { code: 'APS225J2', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APS225J3', desc: 'EO for 225 x 225mm junction', mainDia: '225mm', branchDia: '225mm' },
      { code: 'APS300J1', desc: 'EO for 300 x 100mm junction', mainDia: '300mm', branchDia: '100mm' },
      { code: 'APS300J2', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APS300J3', desc: 'EO for 300 x 225mm junction', mainDia: '300mm', branchDia: '225mm' },
      { code: 'APS300J4', desc: 'EO for 300 x 300mm junction', mainDia: '300mm', branchDia: '300mm' },
    ],
  },
  {
    key: 'sjunc-ultrarib', label: 'Ultrarib', pipeType: 'ultra',
    items: [
      { code: 'APU225J1', desc: 'EO for 225 x 100mm junction', mainDia: '225mm', branchDia: '100mm' },
      { code: 'APU225J2', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APU225J3', desc: 'EO for 225 x 225mm junction', mainDia: '225mm', branchDia: '225mm' },
      { code: 'APU300J2', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APU300J3', desc: 'EO for 300 x 225mm junction', mainDia: '300mm', branchDia: '225mm' },
      { code: 'APU300J4', desc: 'EO for 300 x 300mm junction', mainDia: '300mm', branchDia: '300mm' },
    ],
  },
  {
    key: 'sjunc-ridgisewer', label: 'Ridgisewer', pipeType: 'rigi',
    items: [
      { code: 'APRS150J1', desc: 'EO for 150 x 150mm junction', mainDia: '150mm', branchDia: '150mm' },
      { code: 'APRS225J1', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APRS300J1', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APRS400J1', desc: 'EO for 400 x 150mm junction', mainDia: '400mm', branchDia: '150mm' },
      { code: 'APRS450J1', desc: 'EO for 450mm Saddle',         mainDia: '450mm', branchDia: '' },
      { code: 'APRS500J1', desc: 'EO for 500mm Saddle',         mainDia: '500mm', branchDia: '' },
      { code: 'APRS600J1', desc: 'EO for 600mm Saddle',         mainDia: '600mm', branchDia: '' },
      { code: 'APRS750J1', desc: 'EO for 750mm Saddle',         mainDia: '750mm', branchDia: '' },
      { code: 'APRS900J1', desc: 'EO for 900mm Saddle',         mainDia: '900mm', branchDia: '' },
    ],
  },
  {
    key: 'sjunc-concrete', label: 'Concrete', pipeType: 'concrete',
    items: [
      { code: 'APM300J1', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APM375J1', desc: 'EO for 375 x 150mm junction', mainDia: '375mm', branchDia: '150mm' },
      { code: 'APM450J1', desc: 'EO for 450 x 150mm junction', mainDia: '450mm', branchDia: '150mm' },
      { code: 'APMCD001', desc: 'Core Drill 525Ø+',            mainDia: '525mm', branchDia: '' },
    ],
  },
];

export const STORM_EO_CONC_BED_ITEMS: Array<{ code: string; desc: string }> = [
  { code: 'AMHEO027', desc: 'EO concrete bed and surround 150mmØ pipe'  },
  { code: 'AMHEO028', desc: 'EO concrete bed and surround 225mmØ pipe'  },
  { code: 'AMHEO029', desc: 'EO concrete bed and surround 300mmØ pipe'  },
  { code: 'AMHEO030', desc: 'EO concrete bed and surround 375mmØ pipe'  },
  { code: 'AMHEO031', desc: 'EO concrete bed and surround 450mmØ pipe'  },
  { code: 'AMHEO032', desc: 'EO concrete bed and surround 525mmØ pipe'  },
  { code: 'AMHEO033', desc: 'EO concrete bed and surround 600mmØ pipe'  },
  { code: 'AMHEO034', desc: 'EO concrete bed and surround 675mmØ pipe'  },
  { code: 'AMHEO035', desc: 'EO concrete bed and surround 750mmØ pipe'  },
  { code: 'AMHEO036', desc: 'EO concrete bed and surround 825mmØ pipe'  },
  { code: 'AMHEO037', desc: 'EO concrete bed and surround 900mmØ pipe'  },
  { code: 'AMHEO038', desc: 'EO concrete bed and surround 1050mmØ pipe' },
  { code: 'AMHEO039', desc: 'EO concrete bed and surround 1200mmØ pipe' },
  { code: 'AMHEO040', desc: 'EO concrete bed and surround 1350mmØ pipe' },
  { code: 'AMHEO041', desc: 'EO concrete bed and surround 1500mmØ pipe' },
  { code: 'AMHEO042', desc: 'EO concrete bed and surround 1800mmØ pipe' },
  { code: 'AMHEO043', desc: 'EO concrete bed and surround 2100mmØ pipe' },
  { code: 'AMHEO044', desc: 'EO concrete bed and surround 2400mmØ pipe' },
];

export const STORM_CONC_PROTECTION_ITEMS: Array<{ code: string; desc: string; dia: number }> = [
  { code: 'AMHEO008', desc: 'Concrete protection Slab 150mmØ',  dia: 150  },
  { code: 'AMHEO009', desc: 'Concrete protection Slab 225mmØ',  dia: 225  },
  { code: 'AMHEO010', desc: 'Concrete protection Slab 300mmØ',  dia: 300  },
  { code: 'AMHEO012', desc: 'Concrete protection Slab 375mmØ',  dia: 375  },
  { code: 'AMHEO013', desc: 'Concrete protection Slab 450mmØ',  dia: 450  },
  { code: 'AMHEO014', desc: 'Concrete protection Slab 525mmØ',  dia: 525  },
  { code: 'AMHEO015', desc: 'Concrete protection Slab 600mmØ',  dia: 600  },
  { code: 'AMHEO016', desc: 'Concrete protection Slab 675mmØ',  dia: 675  },
  { code: 'AMHEO017', desc: 'Concrete protection Slab 750mmØ',  dia: 750  },
  { code: 'AMHEO018', desc: 'Concrete protection Slab 825mmØ',  dia: 825  },
  { code: 'AMHEO019', desc: 'Concrete protection Slab 900mmØ',  dia: 900  },
  { code: 'AMHEO020', desc: 'Concrete protection Slab 1050mmØ', dia: 1050 },
  { code: 'AMHEO021', desc: 'Concrete protection Slab 1200mmØ', dia: 1200 },
  { code: 'AMHEO022', desc: 'Concrete protection Slab 1350mmØ', dia: 1350 },
  { code: 'AMHEO023', desc: 'Concrete protection Slab 1500mmØ', dia: 1500 },
  { code: 'AMHEO024', desc: 'Concrete protection Slab 1800mmØ', dia: 1800 },
  { code: 'AMHEO025', desc: 'Concrete protection Slab 2100mmØ', dia: 2100 },
  { code: 'AMHEO026', desc: 'Concrete protection Slab 2400mmØ', dia: 2400 },
];

export const STORM_EO_ITEMS: Array<{ code: string; desc: string; unit: string }> = [
  { code: 'AMHEO006',  desc: 'EO for working in offsite carriageway',               unit: 'm'  },
  { code: 'AMHEO007',  desc: 'EO trench reinstatement in carriageway',              unit: 'm'  },
  { code: 'AMHEO003B', desc: 'EO construct manhole in existing carriageway',        unit: 'Nr' },
  { code: 'AMHEO003',  desc: 'EO Breaking existing manhole for connection',          unit: 'Nr' },
  { code: 'AMHEO003A', desc: 'EO Construct manhole on existing sewer',              unit: 'Nr' },
];

export const STORM_REDUNDANT_SEWER_ITEMS: Array<{ desc: string; unit: string }> = [
  { desc: 'Grout / grub up redundant sewer - n ex 225mmØ', unit: 'm'  },
  { desc: 'Break out existing manhole, remove from site',   unit: 'Nr' },
];

// Manholes section — replaces the old single generic "EO Hydrobrake" /
// "Penstock" / "Weir walls" placeholder rows with the sheet's actual 4 items.
export const STORM_HYDROBRAKE_PENSTOCK_WEIR_ITEMS: Array<{ code: string; desc: string; unit: string }> = [
  { code: 'APMHW006', desc: 'EO for Hydrobrake, penstock & weir wall 1', unit: 'Nr' },
  { code: 'APMHW007', desc: 'EO for Hydrobrake, penstock & weir wall 2', unit: 'Nr' },
  { code: 'APMHW008', desc: 'EO for Hydrobrake, penstock & weir wall 3', unit: 'Nr' },
  { code: 'APMHW009', desc: 'EO for Hydrobrake, penstock & weir wall 4', unit: 'Nr' },
];

// New standalone section, sibling to Manholes — no evaluate codes on the
// source sheet for these two rows.
export const STORM_FILTER_DRAIN_ITEMS: Array<{ desc: string; unit: string }> = [
  { desc: "150mmØ pipe, GBS, b'fill with  gravel- dti ne 1.00m dp", unit: 'm'  },
  { desc: '500mm dia Polyprolplene silt trap dp to sump ne 1.50m',  unit: 'Nr' },
];

// ── Main data builder ─────────────────────────────────────────────────────────

export function buildStormSummaryData(
  pipeRuns: StormSewerRun[],
  manholes: Manhole[],
  laterals: LateralConnection[],
  gullyConnections: GullyConnection[],
  /** Optional per-key manually entered quantities (from eoQtys state or saved build-up) */
  manualQtys?: Record<string, number>,
  manholeScheduleConfig?: { ladders: 'N/A' | 'Stainless' | 'Galvanised'; safetyChains: 'N/A' | 'Stainless' | 'Galvanised' },
): StormSummaryRow[] {
  const ladderCfgLabel = manholeScheduleConfig?.ladders ?? 'N/A';
  const safetyCfgLabel = manholeScheduleConfig?.safetyChains ?? 'N/A';
  const getManual = (key: string): string => {
    const v = manualQtys?.[key];
    return v != null && v > 0 ? String(v) : '';
  };
  // Same as getManual, but permits negative values — for the road-construction
  // Credits rows below, which are entered as negative quantities.
  const getManualSigned = (key: string): string => {
    const v = manualQtys?.[key];
    return v != null && v !== 0 ? String(v) : '';
  };

  // ── Junction helpers ──────────────────────────────────────────────────────
  // NOTE: uses positional indexing — gullyConnections[i] is paired with pipeRuns[i]
  const getStormJunctionQty = (pType: string, mainDia: string, branchDia: string): number => {
    if (!branchDia) return 0;
    return pipeRuns
      .filter((r, i) =>
        (r.pipeType ?? '').toLowerCase() === pType.toLowerCase() &&
        r.diameter === mainDia &&
        gullyConnections[i]?.pipeDiameter === branchDia,
      )
      .reduce((sum, r) => sum + (r.junctions ?? 0), 0);
  };

  const getStormSaddleQty = (mainDia: string): number =>
    pipeRuns
      .filter(
        (r) =>
          (r.pipeType ?? '').toLowerCase() === 'rigi' &&
          r.diameter === mainDia,
      )
      .reduce((sum, r) => sum + (r.junctions ?? 0), 0);

  const getStormCoreDrillQty = (): number =>
    pipeRuns
      .filter(
        (r) =>
          (r.pipeType ?? '').toLowerCase() === 'concrete' &&
          (parseInt((r.diameter ?? '').replace(/mm$/i, ''), 10) || 0) >= 525,
      )
      .reduce((sum, r) => sum + (r.junctions ?? 0), 0);

  // ── 1. Drainage Runs ──────────────────────────────────────────────────────
  const drainageRunItems: StormSummaryRow[] = pipeRuns.map((run, i) => {
    const diamNum = (run.diameter || '').replace(/mm$/i, '');
    const bf = backfillLabel(run.backfillType ?? '');
    const dti = (run.runDepthCategory || 0).toFixed(2);
    const ptCode = pipeTypeEvalCode(run.pipeType ?? '');
    const evalCode = diamNum && run.backfillType
      ? `${ptCode}${diamNum}\\${backfillEvalCode(run.backfillType)}\\${dti}`
      : '';
    const runLength = run.length || 0;
    const refLabel = `${run.fromMH}-${run.toMH}`;
    return {
      key: run.id || `run-${i}`,
      rowType: 'item',
      reference: run.ref || refLabel,
      description: `${refLabel} - ${diamNum}mmØ, GBS, backfill with ${bf}, cart surplus on site - dti ${dti}m.`,
      quantity: runLength.toFixed(3),
      unit: 'm',
      evaluateCode: evalCode || undefined,
      formulaInfo: {
        label: `Run ${run.fromMH}–${run.toMH}`,
        formula: `Pipe run length · ${diamNum}mmØ ${run.pipeType ?? ''} · backfill: ${bf} · dti: ${dti}m`,
        rows: [{ ref: `${run.fromMH}–${run.toMH}`, value: runLength, unit: 'm' }],
        total: runLength,
      },
    };
  });

  // ── 2. Rocker / Stub Pipes ────────────────────────────────────────────────
  const stubPipesSection: StormSummaryRow = {
    key: 'stub-pipes',
    rowType: 'pipe-type',
    reference: 'Rocker / Stub Pipes',
    children: STORM_STUB_PIPE_GROUPS.map((grp) => ({
      key: grp.key,
      rowType: 'pipe-type' as StormSummaryRowType,
      reference: grp.label,
      children: grp.items.map((item) => {
        const qty = calcStormRockerQty(pipeRuns, grp.pipeType, item.dia);
        const contributing = pipeRuns.filter(
          (r) =>
            (r.pipeType ?? '').toLowerCase() === grp.pipeType.toLowerCase() &&
            parseInt(r.diameter ?? '', 10) === item.dia,
        );
        return {
          key: `sstub-${item.code}`,
          rowType: 'item' as StormSummaryRowType,
          evaluateCode: item.code || undefined,
          description: item.desc,
          quantity: qty > 0 ? qty.toString() : '',
          unit: 'Nr',
          formulaInfo: {
            label: item.desc,
            formula: `SUMIFS(rockers, pipeType="${grp.pipeType}", diameter=${item.dia}mm)`,
            rows: contributing.map((r) => ({ ref: `${r.fromMH}–${r.toMH}`, value: r.rockers ?? 0, unit: 'Nr' })),
            total: qty,
          } as StormFormulaInfo,
        };
      }),
    })),
  };

  // ── 3. Junctions ─────────────────────────────────────────────────────────
  const junctionChildren: StormSummaryRow[] = STORM_JUNCTION_GROUPS.map((grp) => ({
    key: grp.key,
    rowType: 'pipe-type' as StormSummaryRowType,
    reference: grp.label,
    children: grp.items.map((item) => {
      let qty = 0;
      let formula = '';
      let contributing: StormSewerRun[] = [];

      if (item.branchDia) {
        qty = getStormJunctionQty(grp.pipeType, item.mainDia, item.branchDia);
        contributing = pipeRuns.filter(
          (r, i) =>
            (r.pipeType ?? '').toLowerCase() === grp.pipeType.toLowerCase() &&
            r.diameter === item.mainDia &&
            gullyConnections[i]?.pipeDiameter === item.branchDia,
        );
        formula = `SUMIFS(junctions, pipeType="${grp.pipeType}", mainDia=${item.mainDia}, gullyDia=${item.branchDia})`;
      } else if (grp.pipeType === 'rigi') {
        qty = getStormSaddleQty(item.mainDia);
        contributing = pipeRuns.filter(
          (r) =>
            (r.pipeType ?? '').toLowerCase() === 'rigi' &&
            r.diameter === item.mainDia,
        );
        formula = `Sum junctions from Ridgisewer pipe runs where diameter = ${item.mainDia} (saddle required)`;
      } else if (grp.pipeType === 'concrete') {
        qty = getStormCoreDrillQty();
        contributing = pipeRuns.filter(
          (r) =>
            (r.pipeType ?? '').toLowerCase() === 'concrete' &&
            (parseInt((r.diameter ?? '').replace(/mm$/i, ''), 10) || 0) >= 525,
        );
        formula = 'Sum junctions from Concrete pipe runs where diameter >= 525mm (core drill required)';
      }

      return {
        key: `sjunc-${item.code || item.desc}`,
        rowType: 'item' as StormSummaryRowType,
        evaluateCode: item.code || undefined,
        description: item.desc,
        quantity: qty > 0 ? qty.toString() : '',
        unit: 'Nr',
        formulaInfo: {
          label: item.desc,
          formula,
          rows: contributing.map((r) => ({ ref: `${r.fromMH}–${r.toMH}`, value: r.junctions ?? 0, unit: 'Nr' })),
          total: qty,
        } as StormFormulaInfo,
      };
    }),
  }));

  // Moved under Junctions (below) rather than EO Items — both are concrete
  // quantities tied to junction diameters, not general EO items.
  const stormConcBedGroup: StormSummaryRow = {
    key: 'seo-conc-bed',
    rowType: 'pipe-type',
    reference: 'EO Concrete Bed & Surround',
    children: STORM_EO_CONC_BED_ITEMS.map((item) => ({
      key: `seo-cb-${item.code}`,
      rowType: 'item' as StormSummaryRowType,
      evaluateCode: item.code || undefined,
      description: item.desc,
      quantity: getManual(`seo-cb-${item.code}`),
      unit: 'm',
      formulaInfo: {
        label: item.desc,
        formula: 'Manually entered quantity',
        rows: [],
        total: 0,
      } as StormFormulaInfo,
    })),
  };
  const stormConcProtectGroup: StormSummaryRow = {
    key: 'seo-conc-protect',
    rowType: 'pipe-type',
    reference: 'Concrete Protection Slab',
    children: STORM_CONC_PROTECTION_ITEMS.map((item) => {
      const qty = calcStormConcreteProtection(pipeRuns, item.dia);
      const contributing = pipeRuns.filter((r) => parseInt(r.diameter ?? '', 10) === item.dia);
      return {
        key: `seo-cp-${item.code}`,
        rowType: 'item' as StormSummaryRowType,
        evaluateCode: item.code || undefined,
        description: item.desc,
        quantity: qty > 0 ? qty.toFixed(3) : '',
        unit: 'm',
        formulaInfo: {
          label: item.desc,
          formula: `SUMIF(diameter=${item.dia}mm, concreteProtection)`,
          rows: contributing.map((r) => ({ ref: `${r.fromMH}–${r.toMH}`, value: r.concreteProtection ?? 0, unit: 'm' })),
          total: qty,
        } as StormFormulaInfo,
      };
    }),
  };

  const junctionsSection: StormSummaryRow = {
    key: 'junctions',
    rowType: 'pipe-type',
    reference: 'Junctions',
    children: [...junctionChildren, stormConcBedGroup, stormConcProtectGroup],
  };

  // ── 4. EO Items ───────────────────────────────────────────────────────────
  const eoItemsSection: StormSummaryRow = {
    key: 'eo-items',
    rowType: 'pipe-type',
    reference: 'EO Items',
    children: [
      {
        key: 'seo-misc',
        rowType: 'pipe-type',
        reference: 'Miscellaneous EO Items',
        children: STORM_EO_ITEMS.map((item, idx) => ({
          key: `seo-misc-${idx}`,
          rowType: 'item' as StormSummaryRowType,
          evaluateCode: item.code || undefined,
          description: item.desc,
          quantity: getManual(`seo-misc-${idx}`),
          unit: item.unit,
          formulaInfo: {
            label: item.desc,
            formula: 'Manually entered quantity',
            rows: [],
            total: 0,
          } as StormFormulaInfo,
        })),
      },
    ],
  };

  const redundantSewerSection: StormSummaryRow = {
    key: 'seo-redundant',
    rowType: 'pipe-type',
    reference: 'Removal of Redundant Sewer',
    children: STORM_REDUNDANT_SEWER_ITEMS.map((item, idx) => ({
      key: `seo-redundant-${idx}`,
      rowType: 'item' as StormSummaryRowType,
      description: item.desc,
      quantity: getManual(`seo-redundant-${idx}`),
      unit: item.unit,
    })),
  };

  // ── 5. Manholes ───────────────────────────────────────────────────────────
  const manholeBaseRows: StormSummaryRow[] = manholes.map((mh, i) => {
    const diamNum = (mh.diameter || '').replace(/mm$/i, '');
    const chamberType = mh.easiTrad === 'easi'
      ? ' Wide wall chamber'
      : mh.easiTrad === 'trad' ? 'Traditional chamber' : '';
    const dti = (mh.depthCategory || 0).toFixed(2);
    const chamberCode = mh.easiTrad === 'easi' ? 'Easi' : mh.easiTrad === 'trad' ? 'Trad' : '';
    const evalCode = chamberCode && diamNum ? `${chamberCode}\\${diamNum}\\${dti}` : '';
    return {
      key: mh.id || `smh-${i}`,
      rowType: 'item',
      reference: mh.ref,
      description: `${mh.ref} - ${diamNum}mmØ,${chamberType} dti - ${dti}m`,
      quantity: '1',
      unit: 'Nr',
      evaluateCode: evalCode || undefined,
      formulaInfo: {
        label: mh.ref,
        formula: `Manhole · ${diamNum}mmØ · ${mh.easiTrad ?? ''} chamber · depth to invert: ${dti}m`,
        rows: [{ ref: mh.ref, value: 1, unit: 'Nr' }],
        total: 1,
      },
    };
  });

  const totalLadders   = calcStormLadderMetres(manholes);
  const totalChains    = calcStormChainMetres(manholes);
  const totalAMPS      = manholes.reduce((s, mh) => s + (mh.amps ?? 0), 0);
  const totalBackdrops = manholes.reduce((s, mh) => s + (mh.backdrop ?? 0), 0);
  const totalMuckStop  = manholes.reduce((s, mh) => s + (mh.muckStopper ?? 0), 0);

  const manholeAccessories: StormSummaryRow[] = [
    {
      key: 'smh-ladders', rowType: 'item',
      description: `Manhole ladders (if not incl in manhole price)${ladderCfgLabel !== 'N/A' ? ` - ${ladderCfgLabel}` : ''}`,
      quantity: totalLadders.toFixed(3), unit: 'm',
      formulaInfo: {
        label: 'Manhole Ladders',
        formula: 'Per manhole: N/A in Configure → 0  |  Depth Category ≤ 2.25 → 0  |  else: Invert Level − Sump − 0.15 — summed across all manholes',
        rows: manholes.filter((mh) => (mh.ladders ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.ladders ?? 0, unit: 'm' })),
        total: totalLadders,
      },
    },
    {
      key: 'smh-chains', rowType: 'item',
      description: `Safety Chains${safetyCfgLabel !== 'N/A' ? ` - ${safetyCfgLabel}` : ''}`,
      quantity: totalChains.toFixed(3), unit: 'Nr',
      formulaInfo: {
        label: 'Safety Chains',
        formula: 'Per manhole: N/A in Configure → 0  |  Depth Category ≤ 2.25 → 0  |  else: Invert Level − Sump − 0.15 — summed across all manholes',
        rows: manholes.filter((mh) => (mh.safetyChains ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.safetyChains ?? 0, unit: 'Nr' })),
        total: totalChains,
      },
    },
    {
      key: 'smh-amps', rowType: 'item',
      description: 'AMPS',
      quantity: totalAMPS.toString(), unit: 'Nr',
      formulaInfo: {
        label: 'AMPS',
        formula: 'SUM(amps) across all manholes',
        rows: manholes.filter((mh) => (mh.amps ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.amps ?? 0, unit: 'Nr' })),
        total: totalAMPS,
      },
    },
    {
      key: 'smh-backdrops', rowType: 'item',
      description: 'Backdrops',
      quantity: totalBackdrops.toString(), unit: 'Nr',
      formulaInfo: {
        label: 'Backdrops',
        formula: 'SUM(backdrop) across all manholes',
        rows: manholes.filter((mh) => (mh.backdrop ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.backdrop ?? 0, unit: 'Nr' })),
        total: totalBackdrops,
      },
    },
    {
      key: 'smh-muckstoppers', rowType: 'item',
      description: 'Muckstoppers',
      quantity: totalMuckStop.toString(), unit: 'Nr',
      formulaInfo: {
        label: 'Muckstoppers',
        formula: 'SUM(muckStopper) across all manholes',
        rows: manholes.filter((mh) => (mh.muckStopper ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.muckStopper ?? 0, unit: 'Nr' })),
        total: totalMuckStop,
      },
    },
    { key: 'smh-clegg', rowType: 'item', description: "Clegg testing to sewer b'fill (500mm layers at 30m intervals)", quantity: getManual('smh-clegg'), unit: 'Nr' },
    ...STORM_HYDROBRAKE_PENSTOCK_WEIR_ITEMS.map((item, idx) => ({
      key: `smh-hw-${idx}`,
      rowType: 'item' as StormSummaryRowType,
      description: item.desc,
      quantity: getManual(`smh-hw-${idx}`),
      unit: item.unit,
      evaluateCode: item.code,
    })),
  ];

  const manholeAdjRow: StormSummaryRow = {
    key: 'smh-adjustment',
    rowType: 'pipe-type',
    reference: 'ADJUSTMENT FOR ROAD CONSTRUCTION GREATER THAN 450mm',
    children: [{
      key: 'smh-credits',
      rowType: 'pipe-type',
      reference: 'Credits',
      children: [
        { key: 'smh-adj-trench',   rowType: 'item', description: 'Trench Excavation', quantity: getManualSigned('smh-adj-trench'), unit: 'm3' },
        { key: 'smh-adj-ews',      rowType: 'item', description: 'EWS',               quantity: getManualSigned('smh-adj-ews'),    unit: 'm2' },
        { key: 'smh-adj-disposal', rowType: 'item', description: 'Disposal',          quantity: getManualSigned('smh-adj-disposal'), unit: 'm3' },
        { key: 'smh-adj-backfill', rowType: 'item', description: 'Backfill',          quantity: getManualSigned('smh-adj-backfill'), unit: 'm3' },
      ],
    }],
  };

  // ── Filter Drain — nested sub-group within Manholes, sitting above the
  // "ADJUSTMENT FOR ROAD CONSTRUCTION..." credits group in the source sheet.
  // Manual inputs, no formula backing (no evaluate codes on the source sheet
  // for these two rows).
  const filterDrainGroup: StormSummaryRow = {
    key: 'filter-drain',
    rowType: 'pipe-type',
    reference: 'Filter Drain',
    children: STORM_FILTER_DRAIN_ITEMS.map((item, idx) => ({
      key: `sfilterdrain-${idx}`,
      rowType: 'item' as StormSummaryRowType,
      description: item.desc,
      quantity: getManual(`sfilterdrain-${idx}`),
      unit: item.unit,
    })),
  };

  const manholesSection: StormSummaryRow = {
    key: 'manholes',
    rowType: 'section',
    description: 'Manholes',
    children: [
      ...(manholeBaseRows.length > 0
        ? manholeBaseRows
        : [{ key: 'smh-empty', rowType: 'item' as const, description: 'No manholes entered yet' }]),
      ...manholeAccessories,
      filterDrainGroup,
      manholeAdjRow,
    ],
  };

  // ── 6. House Laterals ─────────────────────────────────────────────────────
  const lateralItems: StormSummaryRow[] = laterals.map((lat, i) => {
    const diamNum = (lat.pipeDiameter || '').replace(/mm$/i, '');
    // House Laterals have no backfill field of their own — the source sheet's
    // description formula reads the paired Main Sewer Run's Backfill column,
    // matched positionally by row (same convention as Gully Laterals).
    const runBackfillType = pipeRuns[i]?.backfillType;
    const bf = backfillLabel(runBackfillType ?? '');
    const dti = (lat.avgDepthCategory || 0).toFixed(2);
    const ptCode = pipeTypeEvalCode(lat.pipeType ?? '');
    const evalCode = diamNum && runBackfillType
      ? `${ptCode}${diamNum}\\${backfillEvalCode(runBackfillType)}\\${dti}`
      : '';
    const latLength = lat.length || 0;
    const mhRef = lat.toManhole || '';
    return {
      key: lat.id || `slat-${i}`,
      rowType: 'item',
      reference: mhRef,
      description: `${diamNum}mmØ, GBS, backfill with ${bf}, cart surplus on site - dti ${dti}m.`,
      quantity: latLength.toFixed(3),
      unit: 'm',
      evaluateCode: evalCode || undefined,
      formulaInfo: {
        label: mhRef,
        formula: `Lateral · ${diamNum}mmØ ${lat.pipeType ?? ''} · backfill: ${bf} · dti: ${dti}m`,
        rows: [{ ref: mhRef, value: latLength, unit: 'm' }],
        total: latLength,
      },
    };
  });

  const nmeQty = laterals.reduce((sum, lat) => sum + (lat.nme ?? 0), 0);
  // The "1200" column holds each chamber's own depth (e.g. 3.25), not a Nr
  // count — so the chamber total is how many laterals have a depth entered,
  // and the depth breakdown below counts how many share each depth.
  const laterals1200 = laterals.filter((lat) => (lat.count1200 ?? 0) > 0);

  // 1200mmØ chamber depth breakdown — av 3.00m deep .. av 6.00m deep, in 0.25m steps.
  const DEPTH_BUCKETS_1200 = Array.from({ length: 13 }, (_, i) => (3 + i * 0.25).toFixed(2));
  const count1200DepthRows: StormSummaryRow[] = DEPTH_BUCKETS_1200.map((d) => {
    const matching = laterals1200.filter((lat) => (lat.count1200 ?? 0).toFixed(2) === d);
    const qty = matching.length;
    return {
      key: `slat-1200-chamber-${d}`,
      rowType: 'item',
      description: `av ${d}m deep`,
      quantity: qty > 0 ? String(qty) : '',
      unit: 'Nr',
      formulaInfo: {
        label: `1200mmØ chamber av ${d}m deep`,
        formula: `COUNT(house laterals) where 1200 column = ${d}m`,
        rows: matching.map((lat) => ({ ref: lat.toManhole || '', value: 1, unit: 'Nr' })),
        total: qty,
      },
    };
  });

  const lateralsSection: StormSummaryRow = {
    key: 'storm-laterals',
    rowType: 'pipe-type',
    reference: 'House Laterals',
    children: [
      ...(lateralItems.length > 0
        ? lateralItems
        : [{ key: 'slat-empty', rowType: 'item' as const, description: 'No house laterals entered yet' }]),
      {
        key: 'slat-nme-chamber', rowType: 'item',
        description: 'NME Chamber',
        quantity: nmeQty > 0 ? String(nmeQty) : '',
        unit: 'Nr',
        formulaInfo: {
          label: 'NME Chamber',
          formula: 'SUM(nme) across all house laterals',
          rows: laterals.filter((lat) => (lat.nme ?? 0) > 0).map((lat) => ({ ref: lat.toManhole || '', value: lat.nme ?? 0, unit: 'Nr' })),
          total: nmeQty,
        },
      },
      {
        key: 'slat-1200-chamber', rowType: 'item',
        description: '1200mmØ chamber',
        quantity: '',
        unit: 'Nr',
      },
      ...count1200DepthRows,
      {
        key: 'slat-b125-cover', rowType: 'item',
        description: 'EO B125 Cover & frame to drives',
        quantity: getManual('slat-b125-cover'), unit: 'Nr',
      },
    ],
  };

  // ── 7. Gully Laterals ─────────────────────────────────────────────────────
  const gullyItems: StormSummaryRow[] = gullyConnections.map((gully, i) => {
    const diamNum = (gully.pipeDiameter || '').replace(/mm$/i, '');
    const ptCode = pipeTypeEvalCode(gully.pipeType ?? '');
    const dti = (gully.avgDepthCategory || 0).toFixed(2);
    // Gully connections have no backfill field of their own — the source sheet's
    // description formula (='Storm Take off'!AI9&"mmØ, GBS, backfill with "&
    // 'Storm Take off'!M9&...) reads the paired Main Sewer Run's Backfill column
    // (M9), matched positionally by row like pipeRuns[i]/laterals[i]/gullies[i].
    const bf = backfillLabel(pipeRuns[i]?.backfillType ?? '');
    const evalCode = ptCode && diamNum ? `${ptCode}${diamNum}\\${dti}` : '';
    const gullyLength = gully.length || 0;
    const label = gully.gullyRef || gully.runRef || `G${i + 1}`;
    return {
      key: gully.id || `sgully-${i}`,
      rowType: 'item',
      reference: label,
      description: `${diamNum}mmØ, GBS, backfill with ${bf}, cart surplus on site - dti ${dti}m.`,
      quantity: gullyLength.toFixed(3),
      unit: 'm',
      evaluateCode: evalCode || undefined,
      formulaInfo: {
        label,
        formula: `Gully lateral length · ${diamNum}mmØ ${gully.pipeType ?? ''} · backfill: ${bf} · dti: ${dti}m`,
        rows: [{ ref: label, value: gullyLength, unit: 'm' }],
        total: gullyLength,
      },
    };
  });

  const totalConcBS    = gullyConnections.reduce((s, g) => s + (g.concBS ?? 0), 0);
  const totalGullyNr   = gullyConnections.reduce((s, g) => s + (g.gulleys ?? 0), 0);
  const totalRocker150 = gullyConnections.reduce((s, g) => s + (g.gulleyIntoManhole ?? 0), 0);

  const gullySection: StormSummaryRow = {
    key: 'gully-laterals',
    rowType: 'pipe-type',
    reference: 'Gully Laterals',
    children: [
      ...(gullyItems.length > 0
        ? gullyItems
        : [{ key: 'sgully-empty', rowType: 'item' as const, description: 'No gully laterals entered yet' }]),
      {
        key: 'sgully-conc-bs', rowType: 'item',
        description: 'Concrete bed & Surround',
        quantity: totalConcBS > 0 ? totalConcBS.toFixed(3) : '',
        unit: 'm',
        formulaInfo: {
          label: 'Concrete bed & Surround',
          formula: 'SUM(IF(avgDepthCategory<=1.25, length, 0)) across all gully laterals',
          rows: gullyConnections.filter((g) => (g.concBS ?? 0) > 0).map((g) => ({ ref: g.gullyRef || g.id, value: g.concBS ?? 0, unit: 'm' })),
          total: totalConcBS,
        },
      },
      {
        key: 'sgully-gully-complete', rowType: 'item',
        description: 'Precast concrete road gully 450x900 inc. grate & frame',
        quantity: totalGullyNr > 0 ? String(totalGullyNr) : '',
        unit: 'Nr',
        formulaInfo: {
          label: 'Precast concrete road gully 450x900 inc. grate & frame',
          formula: 'SUM(junctions + 150mmRocker - junctionOffRun) across all gully laterals',
          rows: gullyConnections.filter((g) => (g.gulleys ?? 0) > 0).map((g) => ({ ref: g.gullyRef || g.id, value: g.gulleys ?? 0, unit: 'Nr' })),
          total: totalGullyNr,
        },
      },
      {
        key: 'sgully-rocker-150', rowType: 'item',
        description: '150mmØ Rocker',
        quantity: totalRocker150 > 0 ? String(totalRocker150) : '',
        unit: 'Nr',
        formulaInfo: {
          label: '150mmØ Rocker',
          formula: 'SUM(gulleyIntoManhole) across all gully laterals',
          rows: gullyConnections.filter((g) => (g.gulleyIntoManhole ?? 0) > 0).map((g) => ({ ref: g.gullyRef || g.id, value: g.gulleyIntoManhole ?? 0, unit: 'Nr' })),
          total: totalRocker150,
        },
      },
      {
        key: 'sgully-gulliblok', rowType: 'item',
        description: 'GulliBlok',
        quantity: getManual('sgully-gulliblok'),
        unit: 'Nr',
      },
      {
        key: 'sgully-emptying', rowType: 'item',
        description: 'Emptying of gulliblok - periodically',
        quantity: getManual('sgully-emptying'),
        unit: 'Nr',
      },
      {
        key: 'sgully-bags', rowType: 'item',
        description: 'Gully Bags',
        quantity: getManual('sgully-bags'),
        unit: 'Nr',
      },
    ],
  };

  // Only two top-level parent sections remain — Drainage Runs and Manholes.
  // Every other former top-level section (Rocker/Stub Pipes, Junctions, EO
  // Items, Removal of Redundant Sewer, House Laterals, Gully Laterals) now
  // nests as a pipe-type group under Drainage Runs.
  const drainageRunsSection: StormSummaryRow = {
    key: 'drainage-runs',
    rowType: 'section',
    description: 'Drainage Runs',
    children: [
      ...(drainageRunItems.length > 0
        ? drainageRunItems
        : [{ key: 'drainage-runs-empty', rowType: 'item' as StormSummaryRowType, description: 'No pipe runs entered yet' }]),
      stubPipesSection,
      junctionsSection,
      eoItemsSection,
      redundantSewerSection,
      lateralsSection,
      gullySection,
    ],
  };

  return pruneZeroQuantityStormRows([
    drainageRunsSection,
    manholesSection,
  ]);
}

// ── Surplus / Backfill tables ────────────────────────────────────────────────
// Mirrors the 'Storm Summary' sheet's AO:AS (Surplus Runs/Lateral/Manholes/
// Gullies/Total) and AU:AY (Backfill Runs/Laterals/Manholes/Gullies/Total)
// columns — docs/R&S-Takeoffsheet.xlsx. One row per pipe run, pairing it with
// its house lateral and gully lateral (laterals[i]/gullyConnections[i], same
// positional convention as buildStormSummaryData) and the manhole at the
// SAME row position in the Manhole Schedule (manholes[i]) — e.g. Storm
// Summary row 1 (pipeRuns[0]) uses Storm Manhole Schedule row 1
// (manholes[0]). This mirrors the source sheet's own L column
// (`=IF('Storm Manhole Schedule'!B9="","",'Storm Manhole Schedule'!B9)`),
// which reads the Manhole Schedule by row position, NOT the Take Off sheet's
// Manhole From/To columns — deliberately independent of `run.fromMH` /
// `run.toMH`.
//
// Several of the source columns contain what look like copy-paste mistakes
// from the Foul Summary sheet rather than a deliberate Storm-specific rule,
// and replicating them verbatim would silently produce wrong numbers, so
// they're corrected here instead:
//   - AU/AV (Backfill Runs/Laterals) reference the 'Foul Take off' sheet
//     (not 'Storm Take off'), and AV additionally freezes every row to a
//     single absolute cell ($V$9/$AC$9/$AB$5). Fixed to read each row's own
//     Storm run/lateral Ø + depth category, matching AO/AP's (correct)
//     pattern.
//   - AQ/AW (Surplus/Backfill Manholes) look up the lateral's free-text
//     Description column (V) / an unlabeled empty column (W) against the
//     manhole schedule — neither is a manhole reference, so on a real sheet
//     this XLOOKUP would just always fall back to 0. Replaced with a direct
//     positional read of manholes[i] — the same fix already applied to the
//     equivalent Foul Summary columns (AG/AL), for the same reason.
//   - Total Surplus (AS) sums 4 cells on some rows and only 3 (missing
//     Gullies) on others in the source sheet. Always sums all 4 here.
//
// NOT changed — replicated deliberately, since it's consistent across both
// the Foul and Storm sheets rather than a one-off slip: "Surplus Lateral"/
// "Surplus Gullies" (AP/AR) and "Backfill Laterals"/"Backfill Gullies"
// (AV/AX) all read SEWER RATES' "Backfill (m3/m)" column, never "Disposal
// Arisings (m3/m)" — i.e. AP and AV (and AR and AX) are the same formula.

export interface StormSurplusBackfillRow {
  key: string;
  reference: string;
  description: string;
  runs: number;
  laterals: number;
  manholes: number;
  gullies: number;
  total: number;
  runsFormula: StormFormulaInfo;
  lateralsFormula: StormFormulaInfo;
  manholesFormula: StormFormulaInfo;
  gulliesFormula: StormFormulaInfo;
}

export interface StormSurplusBackfillData {
  surplus: StormSurplusBackfillRow[];
  backfill: StormSurplusBackfillRow[];
  surplusTotal: number;
  backfillTotal: number;
}

const parseStormDiameterMm = (d?: string): number => parseInt((d ?? '').replace(/mm$/i, ''), 10) || 0;

export function buildStormSurplusBackfillData(
  pipeRuns: StormSewerRun[],
  manholes: Manhole[],
  laterals: LateralConnection[],
  gullyConnections: GullyConnection[],
): StormSurplusBackfillData {
  const surplus: StormSurplusBackfillRow[] = [];
  const backfill: StormSurplusBackfillRow[] = [];

  pipeRuns.forEach((run, i) => {
    const lat = laterals[i];
    const gully = gullyConnections[i];
    const fromManhole = manholes[i];

    const runDia = parseStormDiameterMm(run.diameter);
    const runDepth = run.runDepthCategory || 0;
    const runRate = findSewerRate(runDia, runDepth);
    const runLength = run.length || 0;

    const latDia = parseStormDiameterMm(lat?.pipeDiameter);
    const latDepth = lat?.avgDepthCategory || 0;
    const latRate = findSewerRate(latDia, latDepth);
    const latLength = lat?.length || 0;

    const gullyDia = parseStormDiameterMm(gully?.pipeDiameter);
    const gullyDepth = gully?.avgDepthCategory || 0;
    const gullyRate = findSewerRate(gullyDia, gullyDepth);
    const gullyLength = gully?.length || 0;

    const mhDia = parseStormDiameterMm(fromManhole?.diameter);
    const mhDepth = fromManhole?.depthCategory;
    const mhRate = findManholeRateVolume(mhDia || undefined, mhDepth);

    const reference = run.ref || `${run.fromMH}-${run.toMH}`;
    const description = `${run.fromMH}-${run.toMH}`;

    const surplusRuns = runRate.disposalArisings * runLength;
    const surplusLateral = latRate.backfill * latLength; // see NOTE above
    const surplusManholes = mhRate.disposalM3;
    const surplusGullies = gullyRate.backfill * gullyLength; // see NOTE above
    const totalSurplus = surplusRuns + surplusLateral + surplusManholes + surplusGullies;

    const backfillRuns = runRate.backfill * runLength;
    const backfillLaterals = latRate.backfill * latLength;
    const backfillManholes = mhRate.backfillM3;
    const backfillGullies = gullyRate.backfill * gullyLength;
    const totalBackfill = backfillRuns + backfillLaterals + backfillManholes + backfillGullies;

    surplus.push({
      key: run.id || `surplus-${i}`,
      reference,
      description,
      runs: surplusRuns,
      laterals: surplusLateral,
      manholes: surplusManholes,
      gullies: surplusGullies,
      total: totalSurplus,
      runsFormula: {
        label: `Surplus Runs — ${description}`,
        formula: `Disposal Arisings(${runDia}mmØ, dti ${runDepth.toFixed(2)}m) × Length`,
        rows: [
          { ref: 'Disposal Arisings rate', value: runRate.disposalArisings, unit: 'm3/m' },
          { ref: 'Length', value: runLength, unit: 'lm' },
        ],
        total: surplusRuns,
      },
      lateralsFormula: {
        label: `Surplus Lateral — ${description}`,
        formula: `Backfill(${latDia}mmØ, dti ${latDepth.toFixed(2)}m) × Length`,
        rows: [
          { ref: 'Backfill rate', value: latRate.backfill, unit: 'm3/m' },
          { ref: 'Length', value: latLength, unit: 'lm' },
        ],
        total: surplusLateral,
      },
      manholesFormula: {
        label: `Surplus Manholes — ${description}`,
        formula: `Disposal(${fromManhole?.ref ?? '—'}, ${mhDia}mmØ, dti ${(mhDepth ?? 0).toFixed(2)}m)`,
        rows: [
          { ref: 'Disposal rate', value: mhRate.disposalM3, unit: 'm3' },
        ],
        total: surplusManholes,
      },
      gulliesFormula: {
        label: `Surplus Gullies — ${description}`,
        formula: `Backfill(${gullyDia}mmØ, dti ${gullyDepth.toFixed(2)}m) × Length`,
        rows: [
          { ref: 'Backfill rate', value: gullyRate.backfill, unit: 'm3/m' },
          { ref: 'Length', value: gullyLength, unit: 'lm' },
        ],
        total: surplusGullies,
      },
    });

    backfill.push({
      key: run.id || `backfill-${i}`,
      reference,
      description,
      runs: backfillRuns,
      laterals: backfillLaterals,
      manholes: backfillManholes,
      gullies: backfillGullies,
      total: totalBackfill,
      runsFormula: {
        label: `Backfill Runs — ${description}`,
        formula: `Backfill(${runDia}mmØ, dti ${runDepth.toFixed(2)}m) × Length`,
        rows: [
          { ref: 'Backfill rate', value: runRate.backfill, unit: 'm3/m' },
          { ref: 'Length', value: runLength, unit: 'lm' },
        ],
        total: backfillRuns,
      },
      lateralsFormula: {
        label: `Backfill Laterals — ${description}`,
        formula: `Backfill(${latDia}mmØ, dti ${latDepth.toFixed(2)}m) × Length`,
        rows: [
          { ref: 'Backfill rate', value: latRate.backfill, unit: 'm3/m' },
          { ref: 'Length', value: latLength, unit: 'lm' },
        ],
        total: backfillLaterals,
      },
      manholesFormula: {
        label: `Backfill Manholes — ${description}`,
        formula: `Granular Backfill(${fromManhole?.ref ?? '—'}, ${mhDia}mmØ, dti ${(mhDepth ?? 0).toFixed(2)}m)`,
        rows: [
          { ref: 'Backfill rate', value: mhRate.backfillM3, unit: 'm3' },
        ],
        total: backfillManholes,
      },
      gulliesFormula: {
        label: `Backfill Gullies — ${description}`,
        formula: `Backfill(${gullyDia}mmØ, dti ${gullyDepth.toFixed(2)}m) × Length`,
        rows: [
          { ref: 'Backfill rate', value: gullyRate.backfill, unit: 'm3/m' },
          { ref: 'Length', value: gullyLength, unit: 'lm' },
        ],
        total: backfillGullies,
      },
    });
  });

  return {
    surplus,
    backfill,
    surplusTotal: surplus.reduce((s, r) => s + r.total, 0),
    backfillTotal: backfill.reduce((s, r) => s + r.total, 0),
  };
}

// ── Build Up (Ponds & Swales / Cellular / Weholite / Conc Attenuation / Box
// Culverts) summary builder ─────────────────────────────────────────────────
// Turns a flat StormBuildUpRowDef[] template plus saved quant/quantText entries
// into a StormSummaryRow tree — rows marked isHeader become pipe-type group
// headers (e.g. "POND 1", "TANK 2") that collect the items following them;
// ungrouped leading rows stay at the root. Mirrors Foul's `buildBuildUpSummaryData`,
// but each domain's evaluate code is resolved by the caller-supplied
// `resolveEvaluateCode` (Storm's five domains use different lookup keys —
// description for Ponds/Swales/Headwalls/Cellular/Box Culverts, row id for
// Weholite/Conc since their description is supplier-name-templated) rather than
// a persisted field — same reason as Foul's Pump Station/Rising Main: the take-off
// page's own dev-mode display (`StormBuildUpTable.tsx`) derives codes live and
// never writes them back to state.
export function buildStormBuildUpSummaryData(
  rows: StormBuildUpRowDef[],
  buildUp: Record<string, FoulBuildUpRowData> | undefined,
  resolveEvaluateCode: (row: StormBuildUpRowDef) => string,
): StormSummaryRow[] {
  const data = buildUp ?? {};
  const result: StormSummaryRow[] = [];
  let currentGroup: StormSummaryRow | null = null;

  rows.forEach((row) => {
    if (row.isHeader) {
      currentGroup = { key: row.id, rowType: 'pipe-type', reference: row.description, children: [] };
      result.push(currentGroup);
      return;
    }

    // isSubHeader/noQuant rows show no qty/unit/evaluate code — same rendering
    // convention as StormBuildUpTable.tsx's own columns.
    const blankValue = row.isSubHeader || row.noQuant;
    const entry = data[row.id];
    const quant = row.allowTextQuant ? entry?.quantText : (entry?.quant ?? row.defaultQuant);
    const item: StormSummaryRow = {
      key: row.id,
      rowType: 'item',
      description: row.description,
      quantity: blankValue ? '' : (quant != null ? String(quant) : ''),
      unit: blankValue ? '' : row.unit,
      evaluateCode: blankValue ? undefined : (resolveEvaluateCode(row) || undefined),
      // `noQuant` rows are exempted from pruning the same way `isSubHeader`
      // rows are (see pruneZeroQuantityStormRows) — both show no qty/unit/
      // rate/amount by design, so a blank quantity string here isn't "nothing
      // entered", it's "this row was never meant to carry a quantity".
      isSubHeader: row.isSubHeader || row.noQuant,
    };

    if (currentGroup) {
      currentGroup.children!.push(item);
    } else {
      result.push(item);
    }
  });

  return pruneZeroQuantityStormRows(result, false);
}
